import React, { useState, useEffect } from 'react';
import { Form, Input, Select, DatePicker, message } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import supplyChainService, { ShipmentBatch } from '../../services/supplyChainService';
import { FormPageLayout } from '../../components/FormPageLayout';
import { useAutoSave } from '../../hooks/useAutoSave';

const { Option } = Select;

const LogisticsBatchEdit: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [providers, setProviders] = useState<any[]>([]); // Logistics Providers

    const isNew = !id || id === 'new';

    const { triggerSave, state: saveState } = useAutoSave<any>({
        onSave: async (data) => {
            const saveData = { ...data };
            if (saveData.etd) saveData.etd = dayjs(saveData.etd).format('YYYY-MM-DD');
            if (saveData.eta) saveData.eta = dayjs(saveData.eta).format('YYYY-MM-DD');

            if (isNew) {
                const result = await supplyChainService.createShipmentBatch(saveData);
                message.success('Batch Created');
                navigate(`/supply-chain/logistics/batches/${result.data.id}`);
            } else {
                await supplyChainService.updateShipmentBatch(parseInt(id), saveData);
            }
        },
        delay: 1000,
        enabled: !isNew,
        showMessage: true
    });

    useEffect(() => {
        fetchProviders();
        if (!isNew) {
            fetchBatch();
        }
    }, [id]);

    const fetchProviders = async () => {
        try {
            const res = await supplyChainService.getLogisticsProviders();
            setProviders(Array.isArray(res.data) ? res.data : (res.data as any).results || []);
        } catch (e) {
            console.error('Failed to load logistics providers');
        }
    };

    const fetchBatch = async () => {
        setLoading(true);
        try {
            const res = await supplyChainService.getShipmentBatches();
            const batches = Array.isArray(res.data) ? res.data : [];
            const batch = batches.find((b: ShipmentBatch) => b.id === parseInt(id!));

            if (batch) {
                form.setFieldsValue({
                    ...batch,
                    etd: batch.etd ? dayjs(batch.etd) : null,
                    eta: batch.eta ? dayjs(batch.eta) : null
                });
            } else {
                message.error('Batch not found');
                navigate('/supply-chain/logistics');
            }
        } catch (error) {
            message.error('Failed to load batch');
        } finally {
            setLoading(false);
        }
    };

    const handleValuesChange = (_: any, allValues: any) => {
        if (!isNew) {
            triggerSave(allValues);
        }
    };

    const handleManualSave = async () => {
        try {
            const values = await form.validateFields();
            if (isNew) {
                const saveData = { ...values };
                if (saveData.etd) saveData.etd = saveData.etd.format('YYYY-MM-DD');
                if (saveData.eta) saveData.eta = saveData.eta.format('YYYY-MM-DD');
                const result = await supplyChainService.createShipmentBatch(saveData);
                message.success('Batch Created');
                navigate(`/supply-chain/logistics/batches/${result.data.id}`);
            }
        } catch (error) {
            // Validation error
        }
    };

    const getSaveStatus = () => {
        if (saveState.isSaving) return 'saving';
        if (saveState.hasUnsavedChanges) return 'hasChanges';
        if (saveState.lastSavedAt) return 'saved';
        return 'idle';
    };

    return (
        <FormPageLayout
            title={isNew ? 'Create Shipment Batch' : `Batch: ${form.getFieldValue('batch_number')}`}
            breadcrumbs={[
                { title: 'Supply Chain', path: '/supply-chain' },
                { title: 'Logistics', path: '/supply-chain/logistics' },
                { title: isNew ? 'New' : 'Edit' }
            ]}
            saveStatus={getSaveStatus()}
            showSaveButton={isNew}
            onSave={handleManualSave}
            saving={loading}
        >
            <Form
                form={form}
                layout="vertical"
                onValuesChange={handleValuesChange}
            >
                <Form.Item
                    name="batch_number"
                    label="Batch Number"
                    rules={[{ required: true, message: 'Please enter batch number' }]}
                >
                    <Input placeholder="SEA-202501-001" />
                </Form.Item>

                <Form.Item name="carrier" label="Logistics Provider (Carrier)">
                    <Select placeholder="Select carrier">
                        {providers.map(p => (
                            <Option key={p.id} value={p.name}>{p.name}</Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item name="etd" label="ETD (Estimated Time of Departure)">
                    <DatePicker style={{ width: '100%' }} />
                </Form.Item>

                <Form.Item name="eta" label="ETA (Estimated Time of Arrival)">
                    <DatePicker style={{ width: '100%' }} />
                </Form.Item>
            </Form>
        </FormPageLayout>
    );
};

export default LogisticsBatchEdit;
